import React, { useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { Button } from 'react-bootstrap';
import { Dash, Plus } from 'react-bootstrap-icons';
import { useFormik } from 'formik';
import { Booth, BoothJobInfo } from 'app/API';
import * as Yup from 'yup';
import styles from './JobsForm.module.scss';

interface IJobsForm {
    jobs: Array<JobModel>
}

interface JobModel {
    description: string;
    link: string;
}

const jobsFormSchema = Yup.object().shape({
    jobs: Yup.array().of(
        Yup.object().shape({
            description: Yup.string().required("Job description is required"),
            link: Yup.string().url("Invalid URL format")
        })
    ).required(),
});

const jobsFormInitialValues: IJobsForm = {
    jobs: [],
}

interface JobsFormProps {
    booth: Booth;
}

const JobsFormComponent = forwardRef<
    { jobsLength: number, getJobs: () => BoothJobInfo[] },
    JobsFormProps
>(({ booth }, ref) => {
    const prevJobModels = useRef<JobModel[]>([]);

    useImperativeHandle(ref, () => ({
        jobsLength: jobsForm.values.jobs.length,
        getJobs: () => jobsForm.values.jobs.map(x => { return { description: x.description, link: x.link } })
    }));

    const jobsForm = useFormik({
        initialValues: jobsFormInitialValues,
        validationSchema: jobsFormSchema,
        onSubmit: () => { }
    });

    const handleJobChange = (e: any, index: number) => {
        let _jobs = [...jobsForm.values.jobs];
        _jobs[index] = {
            ..._jobs[index],
            description: e.target.value,
        };
        jobsForm.setValues({ jobs: _jobs }, true);
    }

    const handleJobLinkChange = (e: any, index: number) => {
        let _jobs = [...jobsForm.values.jobs];
        _jobs[index] = {
            ..._jobs[index],
            link: e.target.value,
        };
        jobsForm.setValues({ jobs: _jobs }, true);
    }

    const handleJobDelete = (index: number) => {
        jobsForm.values.jobs.splice(index, 1);
        jobsForm.setValues({
            jobs: jobsForm.values.jobs,
        });
    }

    const handleAddNewJob = () => {
        const newValue: JobModel = {
            description: '',
            link: 'https://',
        };

        jobsForm.setValues({
            jobs: [...jobsForm.values.jobs, newValue]
        }, true);
    }

    useEffect(() => {
        if (!booth?.jobs) return;

        const jobModels: JobModel[] = booth.jobs.map((job) => ({
            description: job.description,
            link: job.link,
        }));

        jobsForm.setValues({
            jobs: jobModels,
        });
        prevJobModels.current = [...jobModels];
    }, [booth?.id]);

    return (
        <div>
            {jobsForm.status && (
                <div className='mb-lg-15 alert alert-danger'>
                    <div className='alert-text font-weight-bold'>{jobsForm.status}</div>
                </div>
            )}
            {jobsForm.values.jobs.map((item, index) => (
                <div key={`div-${index}`} className='fv-row my-5 d-flex align-items-center'>
                    <input
                        key={`input-${index}`}
                        className='form-control form-control-lg me-3'
                        placeholder='Enter job title...'
                        value={item?.description || ''}
                        required
                        onChange={(e) => handleJobChange(e, index)}
                        type="text"
                        name={`${index}`}
                        autoComplete='off'
                    />
                    <input
                        key={`input-link-${index}`}
                        className='form-control form-control-lg'
                        placeholder='Enter link'
                        value={item?.link || ''}
                        onChange={(e) => handleJobLinkChange(e, index)}
                        type="url"
                        name={`jobs[${index}].link`}
                        autoComplete='off'
                    />
                    <Button
                        key={`delete-button-${index}`}
                        onClick={() => handleJobDelete(index)}
                        variant="none"
                        size='sm'
                        title='Remove this record'
                        className={styles.deleteIcon}
                    >
                        <Dash size="10px" />
                    </Button>
                    <Button
                        variant="none"
                        style={{
                            visibility: index < jobsForm.values.jobs.length - 1 ? 'hidden' : 'visible'
                        }}
                        size='sm'
                        className={styles.addIcon}
                        onClick={handleAddNewJob}
                        title='Add new record'
                    >
                        <Plus size="10px" />
                    </Button>

                </div>
            ))}

            {jobsForm.values.jobs.length === 0 &&
                <div className='text-center justify-content-center mt-4'>
                    <Button
                        variant="none"
                        size='sm'
                        className={styles.addIcon}
                        onClick={handleAddNewJob}
                        title='Add new record'
                    >
                        <Plus size="10px" /> Add new job
                    </Button>
                </div>}
        </div>
    );
});

export default JobsFormComponent;
